/**
 * The Leadership report's queries and mutations (8 Oct). Every route answers
 * 403 to anyone not on `LEADERSHIP_REPORT_VIEWERS`; the page is only offered
 * to those people (`/auth/me` → `leadership_report`).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addReportNote as sdkAddReportNote,
  getReportRefreshStatus as sdkGetReportRefreshStatus,
  getReportSnapshot as sdkGetReportSnapshot,
  type LeadershipFeedbackEntry,
  type LeadershipNoteRequest,
  listReportSnapshots as sdkListReportSnapshots,
  type ReportRefreshStatus,
  type ReportSnapshot,
  type ReportSnapshotListResponse,
  updateReportNow as sdkUpdateReportNow,
} from "@/lib/api";
import { logger } from "@/lib/logger";

export const leadershipReportKeys = {
  all: ["leadership-report"] as const,
  snapshots: (name: string) => [...leadershipReportKeys.all, name, "snapshots"] as const,
  snapshot: (name: string, id: number) =>
    [...leadershipReportKeys.all, name, "snapshot", id] as const,
  status: (name: string) => [...leadershipReportKeys.all, name, "status"] as const,
};

/** The stored copies, newest first (no HTML). */
export function useReportSnapshots(name: string) {
  return useQuery({
    queryKey: leadershipReportKeys.snapshots(name),
    queryFn: async (): Promise<ReportSnapshotListResponse> => {
      const { data } = await sdkListReportSnapshots({ path: { name }, throwOnError: true });
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

/** One copy with its HTML. A stored copy never changes, so it is never refetched. */
export function useReportSnapshot(name: string, id: number | null) {
  return useQuery({
    queryKey: leadershipReportKeys.snapshot(name, id ?? -1),
    queryFn: async (): Promise<ReportSnapshot> => {
      const { data } = await sdkGetReportSnapshot({
        path: { name, snapshot_id: id ?? -1 },
        throwOnError: true,
      });
      return data;
    },
    enabled: id !== null,
    staleTime: Infinity,
  });
}

/**
 * "Add a note for the IT HelpDesk" (16 Oct): a data point, an update or a
 * suggestion, stored in the Feedback inbox with the page's dates and filters.
 * `retry: false`: the dialog shows the failure.
 */
export function useAddReportNote(name: string) {
  return useMutation({
    mutationFn: async (body: LeadershipNoteRequest): Promise<LeadershipFeedbackEntry> => {
      const { data } = await sdkAddReportNote({ path: { name }, body, throwOnError: true });
      return data;
    },
    retry: false,
    onSuccess: (entry) => {
      logger.debug("[leadership-report] note added", entry.feedback_id);
    },
  });
}

/**
 * The refresh (three times a day since 12 Oct): when the data was pulled, the
 * next slot, a failed last run, and whether this reader gets "Update now".
 * Polled every minute (every 10 s while a run is going); the snapshot list
 * and the interactive numbers follow when a new pull lands.
 */
export function useReportRefreshStatus(name: string) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: leadershipReportKeys.status(name),
    queryFn: async (): Promise<ReportRefreshStatus> => {
      const { data } = await sdkGetReportRefreshStatus({ path: { name }, throwOnError: true });
      const known = queryClient.getQueryData<ReportRefreshStatus>(
        leadershipReportKeys.status(name),
      );
      if (known && known.last_success_at !== data.last_success_at) {
        void queryClient.invalidateQueries({ queryKey: leadershipReportKeys.snapshots(name) });
        void queryClient.invalidateQueries({
          queryKey: [...leadershipReportKeys.all, name, "insights"],
        });
      }
      return data;
    },
    refetchInterval: (query) => (query.state.data?.running === true ? 10_000 : 60_000),
    staleTime: 30_000,
  });
}

/** "Update now" (Rusty only): starts the same job in the background; the status shows it running. */
export function useUpdateReportNow(name: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await sdkUpdateReportNow({ path: { name }, throwOnError: true });
      return data;
    },
    onSuccess: () => {
      logger.debug("[leadership-report] Update now started");
      void queryClient.invalidateQueries({ queryKey: leadershipReportKeys.status(name) });
    },
  });
}
